import { isEmpty, isNotDefined } from '@sgme/fp';
import { Dropdown } from '@sgme/ui';
import { type ReactElement, useCallback, useRef } from 'react';
import { v4 as uuid, v4 } from 'uuid';

import { BACKGROUND_LIGHT_COLOR } from '@/App/tiles/FlexiForward/constants.ts';
import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { PredealChecksIndicator } from '@/App/tiles/shared/PredealChecksIndicator.tsx';
import ToggleProduct from '@/App/tiles/ToggleProduct.tsx';
import { ChangeClientDropDownItem } from '@/components/ClientSelector/ChangeClientDropDownItem.tsx';
import { TileOverridenClientIndicator } from '@/components/ClientSelector/TileOverridenClientIndicator.tsx';
import CurrencyPairPicker from '@/components/ui/CurrencyPairPicker/index.tsx';
import { useCheckFlexiForwardByTradeCaptureMutation } from '@/store/flexi-forward/api';
import { flexiForwardClientChanged, resetFlexiForward } from '@/store/flexi-forward/slice.ts';
import { resetFlexiForwardThunk } from '@/store/flexi-forward/thunks';
import { useAppDispatch } from '@/store/hooks.ts';
import { duplicateTile, removeTile } from '@/store/tiles/slice.ts';

export function FlexiForwardHeader({ tileId }: { tileId: string }): ReactElement {
  const dispatch = useAppDispatch();

  const dropdownToggleRef = useRef<HTMLButtonElement>(null);

  const { clientId, currencyPair, predealChecks, globalPreDealCheck } = useFlexiForwardSelector((flexiForward) => {
    const { clientId, properties, tradeCapture } = flexiForward;

    const predealChecks =
      flexiForward.properties.rfs?.status === 'PRICING' ? flexiForward.properties.rfs.quote.predealChecks : undefined;
    const globalPreDealCheck = isNotDefined(predealChecks)
      ? 'Unknown'
      : predealChecks.filter((check) => check.type === 'Icon' && check.statusHealth === 'Nok').length > 0
        ? 'Nok'
        : 'Ok';

    return {
      clientId,
      currencyPair: properties.currencyPair,
      tradeCapture,
      predealChecks,
      globalPreDealCheck,
    };
  });

  const [checkFlexiForward] = useCheckFlexiForwardByTradeCaptureMutation();

  const handleCurrencyPairChange = useCallback(
    (value: string) => {
      dispatch(resetFlexiForward({ tileId, clientId }));

      checkFlexiForward({
        tileId,
        tradeCapture: {
          sessionId: v4(),
          status: 'INITIALIZED',
          version: -1,
        },
        properties: {
          currencyPair: value,
          upfront: 0,
        },
      });
    },
    [checkFlexiForward, tileId, clientId],
  );

  const handleDuplicate = () => {
    dispatch(duplicateTile({ sourceTileId: tileId, newTileId: uuid() }));
  };

  const handleReset = useCallback(() => {
    dispatch(resetFlexiForwardThunk({ tileId }));
  }, [tileId]);

  const handleClose = useCallback(() => {
    dispatch(removeTile(tileId));
  }, [tileId]);

  const handleSelectClient = (clientId: number) => {
    dispatch(flexiForwardClientChanged({ tileId, clientId, isTabClientOverridden: true }));
  };

  const handleClearSelectedClient = () => {
    dispatch(flexiForwardClientChanged({ tileId, clientId: undefined, isTabClientOverridden: true }));
  };

  return (
    <>
      <TileOverridenClientIndicator clientId={clientId} onRemoveOverride={handleClearSelectedClient} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }} className="pb-8px">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ToggleProduct tileId={tileId} />
          <PredealChecksIndicator predealChecks={predealChecks} globalPreDealCheck={globalPreDealCheck} />
        </div>

        <CurrencyPairPicker
          tradeType="FlexiForward"
          value={currencyPair}
          onChange={handleCurrencyPairChange}
          data-e2e="currency-pair-input"
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Dropdown color={BACKGROUND_LIGHT_COLOR} direction="auto" size="md" data-e2e="tile-menu">
            <Dropdown.Toggle className="btn-icon" data-e2e="tileMenu-btn">
              <i className="icon" ref={dropdownToggleRef}>
                more_vert
              </i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <ChangeClientDropDownItem triggerRef={dropdownToggleRef} onSelectClient={handleSelectClient} />
              <Dropdown.Item onClick={handleDuplicate} state={isEmpty(currencyPair) ? 'disabled' : undefined}>
                Duplicate
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleReset}
                state={isEmpty(currencyPair) ? 'disabled' : undefined}
                data-e2e="resetTile-menu-item"
              >
                Reset
              </Dropdown.Item>
              <Dropdown.Item onClick={handleClose} data-e2e="close-tile-menu">
                Close
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </>
  );
}
